import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, Grid, List, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', name: 'All Products' },
  { id: 'petwear', name: 'Petwear' },
  { id: 'peoplewear', name: 'People Wear' },
  { id: 'matching', name: 'Matching Sets' },
];

// Default products for display
const DEFAULT_PRODUCTS = [
  {
    id: '1',
    name: 'Cozy Navy Crew Sweater',
    description: 'Premium cotton blend sweater for your pup.',
    price: 45.00,
    category: 'petwear',
    image_url: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=500',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: ['Navy', 'Cream', 'Tan'],
    in_stock: true,
    featured: true,
  },
  {
    id: '2',
    name: 'Matching Human Crew Hoodie',
    description: "Ultra-soft hoodie that matches your best friend's sweater.",
    price: 85.00,
    category: 'peoplewear',
    image_url: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=500',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    colors: ['Navy', 'Cream', 'Tan'],
    in_stock: true,
    featured: true,
  },
  {
    id: '3',
    name: 'The Perfect Match Bundle',
    description: 'One sweater for your pooch, one hoodie for you.',
    price: 120.00,
    category: 'matching',
    image_url: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500',
    sizes: ['XS-S', 'S-M', 'M-L', 'L-XL'],
    colors: ['Navy', 'Cream'],
    in_stock: true,
    featured: true,
  },
  {
    id: '4',
    name: 'Fleece Dog Vest',
    description: 'Lightweight fleece vest for active pups.',
    price: 38.00,
    category: 'petwear',
    image_url: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=500',
    sizes: ['XS', 'S', 'M', 'L'],
    colors: ['Tan', 'Navy'],
    in_stock: true,
    featured: false,
  },
  {
    id: '5',
    name: 'Classic Crewneck Tee',
    description: 'Everyday comfort with the PoochCrew logo.',
    price: 35.00,
    category: 'peoplewear',
    image_url: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: ['Cream', 'Navy', 'White'],
    in_stock: true,
    featured: false,
  },
  {
    id: '6',
    name: 'Adventure Bandana Set',
    description: 'Matching bandanas for you and your furry friend.',
    price: 28.00,
    category: 'matching',
    image_url: 'https://images.unsplash.com/photo-1544568100-847a948585b9?w=500',
    sizes: ['One Size'],
    colors: ['Navy Pattern', 'Tan Pattern'],
    in_stock: true,
    featured: true,
  },
];

export const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const categoryParam = searchParams.get('category') || 'all';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = categoryParam !== 'all' ? { category: categoryParam } : {};
        const response = await axios.get(`${API}/products`, { params });
        if (response.data && response.data.length > 0) {
          setProducts(response.data);
        } else {
          // Filter default products by category
          const filtered = categoryParam === 'all' 
            ? DEFAULT_PRODUCTS 
            : DEFAULT_PRODUCTS.filter(p => p.category === categoryParam);
          setProducts(filtered);
        }
      } catch (error) {
        console.log('Using default products');
        const filtered = categoryParam === 'all' 
          ? DEFAULT_PRODUCTS 
          : DEFAULT_PRODUCTS.filter(p => p.category === categoryParam);
        setProducts(filtered);
      }
      setLoading(false);
    };
    fetchProducts();
  }, [categoryParam]);

  const handleCategoryChange = (category) => {
    if (category === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-[#f5f0e6]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1
            data-testid="shop-title"
            className="font-heading text-4xl md:text-5xl font-bold text-[#1e3a5f] mb-4"
          >
            Shop Collection
          </h1>
          <p className="font-body text-lg text-[#1e3a5f]/70 max-w-xl">
            Premium matching apparel for you and your best friend. Find the perfect look for every adventure.
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-[#c68b4d]/20">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  data-testid={`category-btn-${category.id}`}
                  onClick={() => handleCategoryChange(category.id)}
                  variant={categoryParam === category.id ? 'default' : 'outline'}
                  className={`rounded-full px-6 ${
                    categoryParam === category.id
                      ? 'bg-[#1e3a5f] text-white'
                      : 'border-[#1e3a5f]/20 text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white'
                  }`}
                >
                  {category.name}
                </Button>
              ))}
            </div>

            {/* View Mode & Sort */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white rounded-full p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-full ${
                    viewMode === 'grid' ? 'bg-[#1e3a5f] text-white' : 'text-[#1e3a5f]'
                  }`}
                  aria-label="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-full ${
                    viewMode === 'list' ? 'bg-[#1e3a5f] text-white' : 'text-[#1e3a5f]'
                  }`}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              <Select defaultValue="featured">
                <SelectTrigger className="w-40 rounded-full border-[#1e3a5f]/20" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f]" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-lg text-[#1e3a5f]/70">No products found in this category.</p>
            </div>
          ) : (
            <div
              className={`grid gap-8 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-1'
              }`}
            >
              {products.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  {/* Image */}
                  <div
                    className={`relative overflow-hidden ${
                      viewMode === 'list' ? 'w-48 flex-shrink-0' : 'h-80'
                    }`}
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {product.featured && (
                      <Badge className="absolute top-4 left-4 bg-[#e76f51] text-white">
                        Featured
                      </Badge>
                    )}
                    {/* Quick add overlay */}
                    <div className="absolute inset-0 bg-[#1e3a5f]/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Button
                        data-testid={`add-to-cart-${product.id}`}
                        className="bg-white text-[#1e3a5f] hover:bg-[#f5f0e6] rounded-full px-6"
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    <p className="font-body text-xs uppercase tracking-wider text-[#c68b4d] mb-2">
                      {product.category === 'peoplewear' ? 'People Wear' : 
                       product.category === 'petwear' ? 'Petwear' : 'Matching Set'}
                    </p>
                    <h3 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-2">
                      {product.name}
                    </h3>
                    <p className="font-body text-sm text-[#1e3a5f]/70 mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="font-heading text-xl font-bold text-[#1e3a5f]">
                        ${product.price.toFixed(2)}
                      </p>
                      <div className="flex gap-1">
                        {product.colors.slice(0, 3).map((color, index) => (
                          <span
                            key={index}
                            className="w-4 h-4 rounded-full border border-[#1e3a5f]/20"
                            style={{
                              backgroundColor:
                                color.toLowerCase() === 'navy' ? '#1e3a5f' :
                                color.toLowerCase() === 'cream' ? '#f5f0e6' :
                                color.toLowerCase() === 'tan' ? '#c68b4d' :
                                color.toLowerCase() === 'white' ? '#ffffff' :
                                '#1e3a5f',
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;
